import { useAppStore } from '@halo/chat-sdk-react';
import { useAudio } from '@halo/hooks';
import type { Msg, Session } from '@halo/chat-sdk';
import { getSessionsUnreadCount, isMyMessage, isUserMsg } from '@halo/chat-sdk';
import { tools } from '@halo/ui-chat-react';
import { useNavigate, useLocation } from 'react-router';
import { Box } from '@chakra-ui/react';
import Paths from '@/configs/router/paths';
import { requestNotificationPermission } from '@/configs/firebase/config';

export function useRequestNotificationPermission() {
  const { user } = useAppStore();

  useEffect(() => {
    if (user?.user_id) {
      requestNotificationPermission();
    }
  }, [user?.user_id]);
}

export function useNotificationNavigation() {
  const navigate = useNavigate();
  const location_ = useLocation();

  const navigateToChat = useCallback(
    (chatId: string) => {
      if (!chatId) return;

      const basePath = Paths.MESSAGES_CHATS;
      const queryParams = chatId ? `/${chatId}` : '';
      const fullUrl = basePath + queryParams;

      if (location_.pathname === basePath) {
        navigate(fullUrl, { replace: true });
      } else {
        navigate(fullUrl);
      }
    },
    [navigate, location_.pathname]
  );

  return { navigateToChat };
}

export function notifyMe(chatId: string, navigateToChat: (chatId: string) => void) {
  if (!chatId) return;

  const basePath = Paths.MESSAGES_CHATS;

  if (document.visibilityState === 'hidden') {
    const notify = new Notification('HaloChat', {
      icon: '/favicon.png',
      body: 'You have a new message',
    });
    notify.onclick = () => {
      notify.close();
      if (window) {
        window.focus();
        navigateToChat(chatId);
      }
    };
    return;
  }

  if (!window.location.pathname.includes(basePath)) {
    tools.notification.info({
      title: <Box>HaloChat</Box>,
      description: (
        <Box
          cursor="pointer"
          onClick={() => {
            navigateToChat(chatId);
            tools.notification.dismiss();
          }}
        >
          You have a new message
        </Box>
      ),
      duration: 2000,
      closable: true,
    });
  }
}

export const useNewMessagesListener = () => {
  const { client, sessions, user } = useAppStore();
  const { t } = useTranslation();
  const { navigateToChat } = useNotificationNavigation();
  const { play: playReceiveSound } = useAudio({ src: '/media/receive.mp3' });
  const [doTitle, setDoTitle] = useState(false);
  const timerId = useRef<any>(null);
  const originalTitle = useRef<string>('');
  const originalIcon = useRef<string>('');
  const newMsgIcon = '/favicon-notification.png';

  const getOriginalDocData = useCallback(() => {
    const iconElement = document.querySelector<HTMLLinkElement>('link[rel="icon"]')!;
    originalTitle.current = document.title;
    originalIcon.current = iconElement.href;
    return {
      originalTitle: originalTitle.current,
      originalIcon: originalIcon.current,
    };
  }, []);

  useEffect(() => {
    getOriginalDocData();
  }, [t, getOriginalDocData]);

  const notifyDocTitle = useCallback(() => {
    clearInterval(timerId.current);
    if (document.visibilityState === 'hidden') {
      const iconElement = document.querySelector<HTMLLinkElement>('link[rel="icon"]')!;
      let flag = true;
      const { unread_count } = getSessionsUnreadCount(sessions);
      timerId.current = setInterval(() => {
        if (unread_count > 0) {
          let newMsgTitle = '';
          if (unread_count > 99) {
            newMsgTitle = `${t('general.notificationOther', { count: '99+' } as any)}`;
          } else {
            newMsgTitle =
              unread_count > 1
                ? t('general.notificationOther', { count: unread_count })
                : t('general.notificationOne', { count: unread_count });
          }
          document.title = flag ? newMsgTitle : originalTitle.current;
          iconElement.href = flag ? newMsgIcon : originalIcon.current;
          flag = !flag;
        } else {
          document.title = originalTitle.current;
          iconElement.href = originalIcon.current;
        }
      }, 2000);
    }

    const handleVisibilityChange = () => {
      if (originalTitle && document.visibilityState === 'visible') {
        const iconElement = document.querySelector<HTMLLinkElement>('link[rel="icon"]')!;
        document.title = originalTitle.current;
        iconElement.href = originalIcon.current;
        clearInterval(timerId.current);
        setDoTitle(false);
      }
    };
    window.removeEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('visibilitychange', handleVisibilityChange);
  }, [sessions, t]);

  useEffect(() => {
    const removeListener = client?.addEventListener(
      'NewMessages',
      async ({ detail }) => {
        const { chat_id, messages } = detail!;
        const canNotifyView = canNewMessagesNotifyView(
          sessions,
          chat_id,
          messages,
          user?.user_id
        );
        if (canNotifyView) {
          playReceiveSound();
          notifyMe(chat_id, navigateToChat);
          setDoTitle(true);
        }
      },
      { once: false }
    );
    return () => {
      removeListener?.();
    };
  }, [client, navigateToChat, notifyDocTitle, playReceiveSound, sessions, user?.user_id]);

  useEffect(() => {
    if (doTitle) {
      notifyDocTitle();
    }
  }, [doTitle, notifyDocTitle]);
};

export const isMuteChat = (sessions: Session[], chat_id?: string) => {
  const matchedSession = sessions.find((session) => session.chat_id === chat_id);
  return {
    isMuted: matchedSession?.operateAccess?.MUTED,
    isMutedAtAll: matchedSession?.operateAccess?.MUTED_AT_ALL,
  };
};

export const canNewMessagesNotifyView = (
  sessions: Session[],
  chat_id = '',
  messages: Msg[] = [],
  user_id?: string
) => {
  const { isMuted, isMutedAtAll } = isMuteChat(sessions, chat_id);
  const hasUserMsg = messages.some((message) => {
    if (isMyMessage(message, user_id)) {
      return false;
    }
    return isUserMsg(message.payload_type, false);
  });
  const canNotify = (!isMuted || !isMutedAtAll) && hasUserMsg;
  return canNotify;
};

export const PANELS_CONFIG = {
  PANELS_NAV_BAR_WIDTH: 208,
  PANELS_NAV_BAR_THRESHOLD_WIDTH: 180,
  PANELS_NAV_BAR_MIN_WIDTH: 94,
  PANELS_NAV_BAR_MAX_WIDTH: 208,
  PANELS_SIDEBAR_WIDTH: 382,
  PANELS_SIDEBAR_MIN_WIDTH: 312,
  PANELS_SIDEBAR_MAX_WIDTH: 506,
};

// const NAV_HIDE_BREAKPOINT = 880;

export const Breakpoints = {
  sm: 480,
  md: 768,
  lg: 992,
  xl: 1280,
  xxl: 1536,
};

export const useAppResponsive = () => {
  const [panelWidths, setPanelWidths] = useState([PANELS_CONFIG.PANELS_NAV_BAR_WIDTH]);
  const [sidebarWidth, setSidebarWidth] = useState([PANELS_CONFIG.PANELS_SIDEBAR_WIDTH]);
  const [hideNav, _setHideNav] = useState(false);
  const lastWidth = useRef(document.documentElement.clientWidth);

  const handleResize = useCallback(() => {
    const width = document.documentElement.clientWidth;
    const isZoomingOut = lastWidth.current >= width;
    if (isZoomingOut) {
      if (width < Breakpoints.xl) {
        setSidebarWidth((prev) => [
          Math.max(prev[0] - (Breakpoints.xl - width), PANELS_CONFIG.PANELS_SIDEBAR_MIN_WIDTH),
        ]);
      }
      if (width < Breakpoints.lg) {
        setPanelWidths((prev) => [
          Math.max(prev[0] - (Breakpoints.lg - width), PANELS_CONFIG.PANELS_NAV_BAR_MIN_WIDTH),
        ]);
      }
    } else {
      if (width >= Breakpoints.xl) {
        setSidebarWidth((prev) => [
          Math.min(prev[0] + (width - Breakpoints.xl), PANELS_CONFIG.PANELS_SIDEBAR_WIDTH),
        ]);
      }
      if (width >= Breakpoints.lg) {
        setPanelWidths((prev) => [
          Math.min(prev[0] + (width - Breakpoints.lg), PANELS_CONFIG.PANELS_NAV_BAR_WIDTH),
        ]);
      }
    }
    // setHideNav(width < NAV_HIDE_BREAKPOINT);
    // console.log('============width', isZoomingOut, width);
    lastWidth.current = width;
  }, []);

  // useEffect(() => {
  //   const width = document.documentElement.clientWidth;
  //   console.log('============navWidths', width, panelWidths, sidebarWidth);
  // }, [panelWidths, sidebarWidth]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  useEffect(() => {
    handleResize();
    const width = document.documentElement.clientWidth;
    if (width < Breakpoints.lg) {
      // setPanelWidths([PANELS_CONFIG.PANELS_NAV_BAR_MIN_WIDTH]);
    }
  }, [handleResize]);

  return {
    hideNav,
    panelWidths,
    setPanelWidths,
    sidebarWidth,
    setSidebarWidth,
  };
};
